import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, TextField, MenuItem } from "@mui/material";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import AppModal from "../../Common/AppModal";

// Import your exact actions
import {
  createSystemDevice,
  createSystemDeviceSuccess,
  updateSystemDevice,
  updateSystemDeviceSuccess,
  getSystemDeviceList,
} from "../../../store/actions"; // Adjust path as needed

export default function AddEditPosSystemModal({
  isOpen,
  setIsOpen,
  toggle,
  isEdit,
  editData, // Pass the single POS system data here when editing
}) {
  const dispatch = useDispatch();

  const initialFormState = {
    Id: "",
    StoreName: "",
    IdExternal: "",
    StoreMetaTitle: "",
    GoogleTagManagerMeasurementId: "",
    FlagLoginScreenShowProfiles: "0",
  };

  const [formData, setFormData] = useState(initialFormState);

  // Redux Selectors mapped to your reducer
  const { createSuccessAPI, createLoading, updateSuccessAPI, updateLoading } =
    useSelector((state) => ({
      createSuccessAPI: state.SystemDevice?.systemDeviceCreate,
      createLoading: state.SystemDevice?.systemDeviceCreateLoading,
      updateSuccessAPI: state.SystemDevice?.systemDeviceUpdate,
      updateLoading: state.SystemDevice?.systemDeviceUpdateLoading,
    }));

  // Populate form if Edit mode, else clear
  useEffect(() => {
    if (isEdit && !isEmpty(editData)) {
      setFormData({
        Id: editData.Id || "",
        StoreName: editData.StoreName || "",
        IdExternal: editData.IdExternal || "",
        StoreMetaTitle: editData.StoreMetaTitle || "",
        GoogleTagManagerMeasurementId:
          editData.GoogleTagManagerMeasurementId || "",
        FlagLoginScreenShowProfiles:
          editData.FlagLoginScreenShowProfiles || "0",
      });
    } else {
      setFormData(initialFormState);
    }
  }, [isEdit, editData]);

  // Handle Input Changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const formRef = useRef(null);

  // Submit Handler
  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      StoreName: formData.StoreName || null,
      IdExternal: formData.IdExternal || null,
      StoreMetaTitle: formData.StoreMetaTitle || null,
      GoogleTagManagerMeasurementId:
        formData.GoogleTagManagerMeasurementId || null,
      FlagLoginScreenShowProfiles: formData.FlagLoginScreenShowProfiles || null,
    };

    if (isEdit) {
      dispatch(updateSystemDevice([{ ...payload, Id: formData.Id }]));
    } else {
      dispatch(createSystemDevice(payload));
    }
  };

  // Listen for Create Success
  useEffect(() => {
    if (isEmpty(createSuccessAPI)) return;

    if (createSuccessAPI?.status === 200) {
      toast.success(
        createSuccessAPI?.message || "POS System Created Successfully!"
      );
      dispatch(getSystemDeviceList()); // Refresh the grid
      setIsOpen(false);
    } else {
      toast.error(createSuccessAPI?.message || "Failed to create POS System.");
    }
    // Clear the success state so it doesn't trigger again
    dispatch(createSystemDeviceSuccess(null));
  }, [createSuccessAPI, dispatch, setIsOpen]);

  // Listen for Update Success
  useEffect(() => {
    if (isEmpty(updateSuccessAPI)) return;

    if (updateSuccessAPI?.status === 200) {
      toast.success(
        updateSuccessAPI?.message || "POS System Updated Successfully!"
      );
      dispatch(getSystemDeviceList()); // Refresh the grid
      setIsOpen(false);
    } else {
      toast.error(updateSuccessAPI?.message || "Failed to update POS System.");
    }
    // Clear the success state so it doesn't trigger again
    dispatch(updateSystemDeviceSuccess(null));
  }, [updateSuccessAPI, dispatch, setIsOpen]);

  const isLoading = createLoading || updateLoading;

  return (
    <AppModal
      open={isOpen}
      onClose={toggle}
      title={isEdit ? "Update POS System" : "Create New POS System"}
      icon={<PointOfSaleOutlinedIcon />}
      headerAccent="indigo"
      size="md"
      loading={isLoading}
      loadingText="Saving…"
      submitLabel={isEdit ? "UPDATE SYSTEM" : "SAVE SYSTEM"}
      // requestSubmit(), not handleSubmit(): it fires the form's native
      // validation for the `required` fields and keeps Enter-to-submit working.
      onSubmit={() => formRef.current?.requestSubmit()}
    >
      <form ref={formRef} onSubmit={handleSubmit}>
      <Grid container spacing={3}>
            {/* Row 1 */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Store Name"
                name="StoreName"
                value={formData.StoreName}
                onChange={handleInputChange}
                fullWidth
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="External ID"
                name="IdExternal"
                value={formData.IdExternal}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              />
            </Grid>

            {/* Row 2 */}
            <Grid item xs={12} md={6}>
              <TextField
                label="Store Meta Title"
                name="StoreMetaTitle"
                value={formData.StoreMetaTitle}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Google Tag Manager ID"
                name="GoogleTagManagerMeasurementId"
                value={formData.GoogleTagManagerMeasurementId}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              />
            </Grid>

            {/* Row 3 */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label="Show Profiles on Login"
                name="FlagLoginScreenShowProfiles"
                value={formData.FlagLoginScreenShowProfiles}
                onChange={handleInputChange}
                fullWidth
                variant="outlined"
              >
                <MenuItem value="0">No</MenuItem>
                <MenuItem value="1">Yes</MenuItem>
              </TextField>
            </Grid>
      </Grid>
      </form>
    </AppModal>
  );
}
